import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, FileText, Image as ImageIcon, Loader2, Mic, Trash2, Upload } from "lucide-react";
import { AttestationActions, RingoverCalls } from "./SavClosureExtras";
import { toast } from "sonner";
import { api } from "../../lib/api";

const ACCEPT = { attestation: "application/pdf,image/*", vocal: "audio/*", photos: "image/*" };
const ICON = { attestation: FileText, vocal: Mic, photos: ImageIcon };

function useBlobUrl(savId, f) {
  const [url, setUrl] = useState(null);
  React.useEffect(() => {
    let u;
    if (f.kind === "attestation") return undefined;
    api.get(`/sav/${savId}/files/${f.id}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [savId, f.id, f.kind]);
  return url;
}

function FileItem({ savId, f, onChanged }) {
  const url = useBlobUrl(savId, f);
  const open = async () => {
    const r = await api.get(`/sav/${savId}/files/${f.id}`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  const del = async () => {
    if (!window.confirm(`Supprimer « ${f.filename} » ?`)) return;
    try { await api.delete(`/sav/${savId}/files/${f.id}`); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };
  return (
    <div className="flex items-center gap-2 text-xs border border-norea-border rounded px-2 py-1.5 bg-white group" data-testid={`sav-file-${f.id}`}>
      {f.kind === "photos" && url ? <img src={url} alt={f.filename} className="w-10 h-10 object-cover rounded" />
        : f.kind === "vocal" && url ? <audio controls src={url} className="h-8 max-w-[220px]" />
          : <button onClick={open} className="text-norea-primary hover:underline truncate max-w-[220px]">{f.filename}</button>}
      <span className="text-norea-textMuted flex-1 min-w-0"><span className="truncate block">{f.kind !== "attestation" && f.filename} · {f.source}</span>
        {(f.summary || f.transcript) && <span className="block text-[10px] line-clamp-2" data-testid={`sav-file-transcript-${f.id}`}>« {f.summary || f.transcript} »</span>}</span>
      <button onClick={del} className="p-1 text-norea-textMuted hover:text-red-700 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-testid={`sav-file-delete-${f.id}`}><Trash2 className="w-3 h-3" /></button>
    </div>
  );
}

function KindBlock({ sav, kind, label, onChanged, children }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const files = (sav.files || []).filter((f) => f.kind === kind);
  const Icon = ICON[kind];
  const upload = async (list) => {
    setBusy(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", file);
        await api.post(`/sav/${sav.id}/files`, fd);
      }
      toast.success("Pièce déposée");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Dépôt impossible"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="space-y-2" data-testid={`sav-closure-${kind}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium inline-flex items-center gap-1.5"><Icon className="w-4 h-4 text-norea-primary" />{label}
          <span className={`text-[10px] px-1.5 rounded ${files.length ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{files.length ? "présent" : "manquant"}</span></span>
        <button onClick={() => ref.current?.click()} disabled={busy} className="btn-ghost text-xs" data-testid={`sav-upload-${kind}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Déposer</button>
        <input ref={ref} type="file" hidden multiple={kind === "photos"} accept={ACCEPT[kind]} onChange={(e) => e.target.files?.length && upload([...e.target.files])} data-testid={`sav-upload-input-${kind}`} />
      </div>
      {children}
      {files.map((f) => <FileItem key={f.id} savId={sav.id} f={f} onChanged={onChanged} />)}
    </div>
  );
}

export default function SavClosure({ sav, kinds, onChanged }) {
  const [state, setState] = useState(null);
  const loadState = () => api.get(`/sav/${sav.id}/closure`).then((r) => setState(r.data)).catch(() => {});
  useEffect(() => { loadState(); }, [sav]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = state && state.missing.length === 0;
  return (
    <section className="card-flat p-5 space-y-4" data-testid="sav-closure">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-base font-medium">Clôture prouvée</h3>
          <p className="text-[11px] text-norea-textMuted">Le statut « Fini » n'est possible qu'avec les 3 pièces. L'email de clôture au client (avec l'attestation) est alors préparé dans « À approuver ».</p>
        </div>
        {state && <span className={`text-xs px-2 py-1 rounded border inline-flex items-center gap-1 shrink-0 ${done ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="sav-closure-state">
          {done ? <><CheckCircle2 className="w-3.5 h-3.5" />3/3 pièces — clôture possible</> : `${3 - state.missing.length}/3 pièces`}</span>}
      </div>
      {Object.entries(kinds).map(([k, l]) => (
        <KindBlock key={k} sav={sav} kind={k} label={l} onChanged={onChanged}>
          {k === "attestation" && <AttestationActions sav={sav} state={state} reload={loadState} />}
          {k === "vocal" && <RingoverCalls sav={sav} state={state} onAttached={onChanged} />}
        </KindBlock>
      ))}
    </section>
  );
}
